import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { cn } from '@packages/shared/src/utils/cn/cn';
import { Link } from '@packages/modules/src/Link/Link';
import { useIsBot } from '@packages/tracking/src/hooks/useIsBot/useIsBot';
import { Chip } from '@packages/shared/src/ui/Chip/Chip';

import {
  buildCategoryTree,
  type CategoryNodeWithChildren,
} from '../../utils/buildCategoryTree/buildCategoryTree';
import { CategoryMessages } from './categoryTreeMessages';

export type CategoryTreeItem = Omit<CategoryItemProps, 'onClick'> & {
  id: string;
};
export type CategoryTreeMobileFirstProps = {
  /** The current category tree, flattened and sorted by selection */
  categories: CategoryTreeItem[];
  /** If `true`, shows a dedicated "clear all" interaction/button in addition to the root level category */
  showClearAll?: boolean;
  /** Callback fired when a category is clicked or all categories are cleared */
  onNavigate?: () => void;
  categoryDescription?: ReactNode;
};

type CategoryItemProps = {
  /** Link to the page for the category */
  href: string;
  /** name of the category */
  name: string;
  /** hierarchical level in the category tree, translates to indentation */
  level: number;
  /** count of total items in the current search result that match this category */
  count?: number | null;
  /** Is the item currently selected, whether indirectly (child is selected) or directly */
  isSelected?: boolean | null;
  /** Is the item currently **directly** selected (not by hierarchical parentage) */
  isSelectedExact?: boolean | null;
  /** A callback that is invoked when the item is clicked */
  onClick?: () => void;
};

/** A single category item for the list
 *
 * Indents itself based on the level of the category.
 *
 * Different styling is applied for parent nodes and selected nodes.
 */
const CategoryItem = ({
  name,
  count,
  href,
  isSelected,
  isSelectedExact,
  onClick,
  level,
}: CategoryItemProps) => {
  const { formatMessage } = useIntl();
  const isBot = useIsBot();

  const categoryLevelPaddingLeftMap: Record<number, string> = {
    0: 'pl-1',
    1: 'pl-2',
    2: 'pl-3',
    3: 'pl-4',
    4: 'pl-5',
    5: 'pl-6',
  };
  const Item = (
    <div className="flex flex-1 items-center justify-between px-1 py-1">
      <span
        className={cn(
          'text-size-body-sm overflow-hidden py-0.5 hyphens-auto',
          isSelected ? 'font-bold' : 'font-normal',
          categoryLevelPaddingLeftMap[level] || '',
        )}
      >
        {name}
      </span>
      {isSelectedExact && <span className="icon-emp-checkmark text-color-primary size-4" />}
      {!isSelected && count && count > 0 && (
        <Chip variant="filled" className="break-none mx-0.5 min-h-3 border-none text-nowrap">
          <span aria-hidden className="px-1">
            {count}
          </span>
          <span className="sr-only">
            {formatMessage(CategoryMessages.itemsQuantity, { count })}
          </span>
        </Chip>
      )}
    </div>
  );

  return (
    <>
      {isBot ? (
        Item
      ) : (
        <Link
          className="border-b-color-separator hover:bg-color-grey-light block border-b text-inherit no-underline -outline-offset-1"
          href={href}
          onClick={() => {
            onClick?.();
          }}
          aria-current={isSelected ? 'page' : undefined}
        >
          {Item}
        </Link>
      )}
    </>
  );
};

type CategoryWithChildren = CategoryNodeWithChildren<CategoryTreeItem>;

const RecursiveCategoryItem = ({
  category,
  onNavigate,
}: {
  category: CategoryWithChildren;
  onNavigate?: () => void;
}) => {
  const hasChildren = category.children.length > 0;

  return (
    <li>
      <CategoryItem {...category} onClick={onNavigate} />
      {hasChildren && (
        <ul className="list-none">
          {category.children.map((child) => (
            <RecursiveCategoryItem key={child.id} category={child} onNavigate={onNavigate} />
          ))}
        </ul>
      )}
    </li>
  );
};

/**
 * Displays a single level of categories as navigation items, including upwards if possible.
 * */
export const CategoryTreeMobileFirst = ({
  categories: flatCategories = [],
  showClearAll,
  onNavigate,
  categoryDescription,
}: CategoryTreeMobileFirstProps) => {
  const rootCategoryHref = flatCategories?.[0]?.href ?? '';
  const isBot = useIsBot();
  const canClear = flatCategories.some((category) => category.isSelected);

  const categoryItemsTree = buildCategoryTree(flatCategories);

  const Item = (
    <div className="bg-color-grey-light">
      <span className="text-size-body-sm">
        <FormattedMessage
          key="search.filters.category.clearSelection-text"
          {...CategoryMessages.clearSelection}
        />
      </span>
      <span className="icon-emp-cancel text-color-text-dark size-4" />
    </div>
  );
  return (
    <div className="overflow-auto">
      {categoryDescription && <span>{categoryDescription}</span>}
      <ul className="list-none py-1">
        {categoryItemsTree.map((categoryItem) => (
          <RecursiveCategoryItem
            key={categoryItem.id}
            category={categoryItem}
            onNavigate={onNavigate}
          />
        ))}
      </ul>

      {showClearAll && canClear && (
        <div className="px-2 py-1">
          {isBot ? (
            Item
          ) : (
            <Link
              href={rootCategoryHref}
              className="bg-color-grey-dark no-underline"
              onClick={onNavigate}
            >
              {Item}
            </Link>
          )}
        </div>
      )}
    </div>
  );
};
